import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import CustomCalendar from "../components/CustomerCalendar";

const AshramDailySchedule = () => {
  const [preprocessedSchedule, setPreprocessedSchedule] = useState({});
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialType = queryParams.get("type") || "A";
  const [scheduleType] = useState(initialType);
  const hasFetched = useRef(false);

  useEffect(() => {
    const fetchData = async () => {
      if (hasFetched.current) return; // Prevent duplicate calls
      hasFetched.current = true;
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}app/hrd2/getJsonDataFromStoreProc?spName=ashram_app_daily_schedule_data&param1=type|${scheduleType}|string`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const outerData = await response.json();
        const parsedData = JSON.parse(outerData.response);
        const processedSchedule = preprocessEvents(parsedData.data);
        setPreprocessedSchedule(processedSchedule);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [scheduleType]);

  const preprocessEvents = (events) => {
    const schedule = {};
    events?.forEach((event) => {
      const dateStr = event.date_of_year;
      const timestampMatch = dateStr.match(/\/Date\((\d+)\)\//);

      if (timestampMatch) {
        const timestamp = parseInt(timestampMatch[1], 10);
        const gmtOffset = 5.5 * 3600 * 1000;
        const adjustedTimestamp = timestamp + gmtOffset;
        const eventDate = new Date(adjustedTimestamp);
        const formattedDate = eventDate.toISOString().split("T")[0];

        if (!schedule[formattedDate]) {
          schedule[formattedDate] = [];
        }
        schedule[formattedDate].push(event);
      }
    });
    return schedule;
  };

  const selectedDateString = selectedDate.toISOString().split("T")[0];
  const dailySchedule = preprocessedSchedule[selectedDateString] || [];

  // Dynamic title based on scheduleType
  const pageTitle =
    scheduleType === "V" ? "Daily Visitor Schedule" : "Ashram Daily Schedule";
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Shrimad Rajchandra Mission Dharampur",
    alternateName: "(SRMD)",
    url: "https://www.srmd.org/",
    logo: "https://d3fzplfbemxxxv.cloudfront.net/srmd/wp-content/uploads/2019/09/06090806/mission_logo.svg",
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "7285040000",
      contactType: "customer service",
      areaServed: ["IN", "150", "002", "035", "419"],
      availableLanguage: "en",
    },
    sameAs: [
      "https://www.facebook.com/shrimadrajchandramissiondharampur",
      "https://twitter.com/srmdharampur",
      "https://www.instagram.com/srmdharampur/",
      "https://www.youtube.com/shrimadrajchandramissiondharampur",
      "https://www.linkedin.com/company/shrimad-dharampur",
      "https://in.pinterest.com/SRMissionDharampur/",
      "https://vimeo.com/srmissiondharampur",
      "https://srmd.link/",
      "https://srmd.link/SRMDTikTok",
      "https://srmdyoga.org/",
      "https://srmdcourses.org/",
    ],
  };
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://www.srmd.org/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Ashram Daily Schedule",
        item: "https://www.srmd.org/AshramDailySchedule/",
      },
    ],
  };
  const webpageSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": "https://www.srmd.org/AshramDailySchedule/#webpage",
        url: "https://www.srmd.org/AshramDailySchedule/",
        name: "Shrimad Rajchandra Ashram Daily Schedule | Timings & Activities",
        isPartOf: {
          "@id": "https://www.srmd.org/#website",
        },
        about: {
          "@id": "https://www.srmd.org/ashram/#place",
        },
        publisher: {
          "@id": "https://www.srmd.org/#organization",
        },
      },
      {
        "@type": "Place",
        "@id": "https://www.srmd.org/ashram/#place",
        name: "Shrimad Rajchandra Ashram",
        address: {
          "@type": "PostalAddress",
          addressLocality: "Dharampur",
          addressRegion: "Gujarat",
          addressCountry: "IN",
        },
      },
      {
        "@type": "Person",
        "@id": "https://www.srmd.org/#gurudevshri-rakeshji",
        name: "Pujya Gurudevshri Rakeshji",
        url: "https://www.srmd.org/pujya-gurudevshri-rakeshji/",
      },
    ],
  };

  return (
    <div className="bg-[#efede7] min-h-screen">
      <Helmet>
        <title>Ashram Daily Schedule & Timings | Shrimad Rajchandra Ashram</title>
        <meta
          name="description"
          content="View the Ashram Daily Schedule at Shrimad Rajchandra Ashram, Dharampur, with timings for meditation, Satsang, Puja, Arti, Devvandan and spiritual activities."
        />
        <link rel="canonical" href="https://www.srmd.org/AshramDailySchedule/" />
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        <script type="application/ld+json">{JSON.stringify(webpageSchema)}</script>
      </Helmet>
      <div className="max-w-6xl mx-auto px-2 sm:px-20 py-4">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-5xl font-playfair font-medium text-[#c66a10] mb-6 text-center sm:text-left">
            {pageTitle}
          </h1>

          <CustomCalendar
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
          />
        </div>

        <div className="bg-white  shadow py-6 ">
          {loading ? (
            <div className="flex justify-center items-center py-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="1.5em"
                height="1.5em"
                viewBox="0 0 24 24"
              >
                <path
                  fill="#c66a10"
                  d="M20.27,4.74a4.93,4.93,0,0,1,1.52,4.61,5.32,5.32,0,0,1-4.1,4.51,5.12,5.12,0,0,1-5.2-1.5,5.53,5.53,0,0,0,6.13-1.48A5.66,5.66,0,0,0,20.27,4.74ZM12.32,11.53a5.49,5.49,0,0,0-1.47-6.2A5.57,5.57,0,0,0,4.71,3.72,5.17,5.17,0,0,1,9.53,2.2,5.52,5.52,0,0,1,13.9,6.45,5.28,5.28,0,0,1,12.32,11.53ZM19.2,20.29a4.92,4.92,0,0,1-4.72,1.49,5.32,5.32,0,0,1-4.34-4.05A5.2,5.2,0,0,1,11.6,12.5a5.6,5.6,0,0,0,1.51,6.13A5.63,5.63,0,0,0,19.2,20.29ZM3.79,19.38A5.18,5.18,0,0,1,2.32,14a5.3,5.3,0,0,1,4.59-4,5,5,0,0,1,4.58,1.61,5.55,5.55,0,0,0-6.32,1.69A5.46,5.46,0,0,0,3.79,19.38ZM12.23,12a5.11,5.11,0,0,0,3.66-5,5.75,5.75,0,0,0-3.18-6,5,5,0,0,1,4.42,2.3,5.21,5.21,0,0,1,.24,5.92A5.4,5.4,0,0,1,12.23,12ZM11.76,12a5.18,5.18,0,0,0-3.68,5.09,5.58,5.58,0,0,0,3.19,5.79c-1,.35-2.9-.46-4-1.68A5.51,5.51,0,0,1,11.76,12ZM23,12.63a5.07,5.07,0,0,1-2.35,4.52,5.23,5.23,0,0,1-5.91.2,5.24,5.24,0,0,1-2.67-4.77,5.51,5.51,0,0,0,5.45,3.33A5.52,5.52,0,0,0,23,12.63ZM1,11.23a5,5,0,0,1,2.49-4.5,5.23,5.23,0,0,1,5.81-.06,5.3,5.3,0,0,1,2.61,4.74A5.56,5.56,0,0,0,6.56,8.06,5.71,5.71,0,0,0,1,11.23Z"
                >
                  <animateTransform
                    attributeName="transform"
                    dur="1.5s"
                    repeatCount="indefinite"
                    type="rotate"
                    values="0 12 12;360 12 12"
                  />
                </path>
              </svg>
            </div>
          ) : dailySchedule.length > 0 ? (
            Object.entries(
              dailySchedule.reduce((acc, event) => {
                const { time_tag } = event;
                if (!acc[time_tag]) acc[time_tag] = [];
                acc[time_tag].push(event);
                return acc;
              }, {})
            ).map(([timeOfDay, activities]) => (
              <div key={timeOfDay} className="mb-8">
                {/* Time of Day Header */}

                <div className="ml-4 sm:ml-24 mb-4 w-fit">
                  <h2 className="text-2xl sm:text-3xl font-playfair font-bold text-[#8e4d0e] pb-1">
                    {timeOfDay}
                  </h2>
                  <div className="h-1 bg-[#8e4d0e] rounded w-[80%]"></div>
                </div>

                {/* Activities Grid */}
                <div className="grid grid-cols-1 divide-y divide-gray-300 sm:border-t border-t-0 border-b">
                  {activities.map((activity, index) => (
                    <div
                      key={index}
                      className={`grid grid-cols-2 sm:grid-cols-3 border-gray-300`}
                    >
                      <div className="p-4 flex-shrink-0 sm:border-r border-gray-300 flex items-center justify-center">
                        <img
                          src={activity.image_path}
                          alt={activity.event_desc}
                          className="w-32 sm:w-40 h-32 sm:h-32 object-cover"
                        />
                      </div>

                      <div className="p-4 col-span-1 sm:col-span-2">
                        <h3 className="text-md sm:text-xl font-medium text-[#8e4d0e] mb-2">
                          {activity.event_desc}
                        </h3>
                        <div className="text-gray-600">
                          <div className="flex items-center gap-1 mb-2 text-[#c66a10]">
                            <svg
                              className="w-5 h-5 flex-shrink-0"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <circle cx="12" cy="12" r="10"></circle>
                              <polyline points="12 6 12 12 16 16"></polyline>
                            </svg>
                            <span className="break-words font-bold text-sm sm:text-lg">
                              {activity.event_time}
                            </span>
                          </div>
                          <div className="flex  items-start  gap-1 text-[#c66a10] -ml-0.5">
                            <svg
                              class="w-6 h-6 flex-shrink-0 self-start currentColor dark:text-white"
                              aria-hidden="true"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <path
                                stroke="currentColor"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M12 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
                              />
                              <path
                                stroke="currentColor"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M17.8 13.938h-.011a7 7 0 1 0-11.464.144h-.016l.14.171c.1.127.2.251.3.371L12 21l5.13-6.248c.194-.209.374-.429.54-.659l.13-.155Z"
                              />
                            </svg>
                            <span className="break-words font-bold text-sm sm:text-lg">
                              {activity.event_venue || "No venue specified"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <p className="text-gray-500 text-center">
              No events scheduled for {selectedDateString}.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AshramDailySchedule;
